import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  Sparkles, Loader2, RefreshCw, Clock, BookOpen, CheckCircle2, Circle,
  GraduationCap, Coffee, FileText, BrainCircuit, ArrowRight,
} from "lucide-react";

const TYPE_ICON = {
  konu_anlatim: BookOpen,
  quiz: BrainCircuit,
  deneme: FileText,
  tekrar: GraduationCap,
  mola: Coffee,
};
const TYPE_LABEL = {
  konu_anlatim: "Konu Anlatımı",
  quiz: "Mini Quiz",
  deneme: "Deneme",
  tekrar: "Tekrar",
  mola: "Mola",
};
const SUBJECT_KEY = {
  "Matematik": "matematik",
  "Türkçe": "turkce",
  "Fen Bilimleri": "fen",
  "T.C. İnkılap Tarihi": "inkilap",
  "Din Kültürü": "din",
  "Din Kültürü ve Ahlak Bilgisi": "din",
  "İngilizce": "ingilizce",
};

export default function DailyPlanPage() {
  const [minutes, setMinutes] = useState(60);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [doneBlocks, setDoneBlocks] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get(`/coach/daily-plan?minutes=${minutes}`).then((r) => r.data),
        api.get("/coach/daily-plan/status").then((r) => r.data),
      ]);
      setPlan(p);
      setDoneBlocks(s.done_blocks || []);
    } catch {
      toast.error("Plan oluşturulamadı");
    } finally {
      setLoading(false);
    }
  };

  const regenerate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/coach/daily-plan/regenerate", { minutes });
      setPlan(r.data);
      setDoneBlocks([]);
      toast.success("Yeni plan hazır!");
    } catch {
      toast.error("Plan yenilenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const markDone = async (idx) => {
    setDoneBlocks((d) => [...new Set([...d, idx])]);
    try {
      await api.post("/coach/daily-plan/block-done", { index: idx });
    } catch {}
  };

  const totalDone = doneBlocks.length;
  const totalBlocks = plan?.bloklar?.length || 0;
  const totalDoneMinutes = (plan?.bloklar || []).filter((_, i) => doneBlocks.includes(i))
    .reduce((s, b) => s + (b.dakika || 0), 0);

  return (
    <div className="space-y-5 ela-fade-in" data-testid="daily-plan-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Bugün</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Ne Çalışayım?</h1>
          <p className="text-sm text-slate-500 mt-1">AI koçun bugüne özel programını hazırladı.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500">Bugün için</div>
          <div className="font-display font-bold text-lg text-slate-900">{minutes}dk</div>
          <Button onClick={regenerate} variant="outline" disabled={loading}
            data-testid="daily-plan-regenerate-btn" className="rounded-xl">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2 hidden sm:inline">Yenile</span>
          </Button>
        </div>
      </div>

      <Card className="p-5 rounded-2xl border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-slate-700">Çalışma süresi: <span className="font-display font-bold">{minutes} dakika</span></div>
        </div>
        <Slider value={[minutes]} min={20} max={180} step={10}
          onValueChange={(v) => setMinutes(v[0])}
          data-testid="minutes-slider"
        />
        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
          <span>20dk</span><span>60dk</span><span>120dk</span><span>180dk</span>
        </div>
      </Card>

      {!plan && !loading && (
        <Card className="p-10 rounded-2xl border-slate-200 text-center">
          <Sparkles className="w-10 h-10 text-[#FF6B6B] mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Plan henüz oluşmadı.</p>
        </Card>
      )}

      {loading && !plan && (
        <Card className="p-10 rounded-2xl border-slate-200 text-center">
          <Loader2 className="w-8 h-8 text-[#FF6B6B] animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm">AI koç bugünün planını oluşturuyor…</p>
        </Card>
      )}

      {plan && (
        <>
          <Card className="p-5 rounded-2xl border-slate-200 bg-rose-50/40">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-[#FF6B6B]" />
              <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Bugünün Teması</span>
            </div>
            <p className="font-heading text-lg font-semibold text-slate-900">{plan.tema}</p>
          </Card>

          <Card className="p-5 rounded-2xl border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-slate-500">İlerleme</div>
                <div className="font-display font-bold text-2xl text-slate-900">
                  {totalDone}<span className="text-slate-400">/{totalBlocks}</span> blok
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Tamamlanan süre</div>
                <div className="font-display font-bold text-2xl text-slate-900">
                  {totalDoneMinutes}<span className="text-slate-400 text-sm">/{plan.toplam_dakika}dk</span>
                </div>
              </div>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#FF6B6B] transition-all"
                style={{ width: totalBlocks ? `${(totalDone / totalBlocks) * 100}%` : "0%" }} />
            </div>
          </Card>

          <div className="space-y-2.5">
            {(plan.bloklar || []).map((b, i) => {
              const Icon = TYPE_ICON[b.tip] || BookOpen;
              const isDone = doneBlocks.includes(i);
              const subjectKey = b.ders ? SUBJECT_KEY[b.ders] : null;
              const link = b.tip === "konu_anlatim" && subjectKey && b.konu_kodu ? `/konu/${subjectKey}/${b.konu_kodu}` :
                           b.tip === "quiz" && subjectKey && b.konu_kodu ? `/konu/${subjectKey}/${b.konu_kodu}` :
                           b.tip === "deneme" ? "/denemeler" : null;

              return (
                <Card key={i} className={`p-4 rounded-2xl border ${isDone ? "border-green-200 bg-green-50/30" : "border-slate-200"}`}
                  data-testid={`plan-block-${i}`}>
                  <div className="flex gap-3 items-start">
                    <button onClick={() => markDone(i)} data-testid={`plan-done-${i}`}
                      className="mt-1 shrink-0">
                      {isDone ? <CheckCircle2 className="w-6 h-6 text-green-600" /> :
                        <Circle className="w-6 h-6 text-slate-300 hover:text-[#FF6B6B] transition" />}
                    </button>
                    <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                      b.tip === "mola" ? "bg-amber-50 text-amber-700" :
                      b.tip === "deneme" ? "bg-slate-900 text-white" :
                      b.tip === "quiz" ? "bg-indigo-50 text-indigo-600" :
                      "bg-rose-50 text-[#FF6B6B]"
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                          {TYPE_LABEL[b.tip] || b.tip}
                        </span>
                        {b.ders && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{b.ders}</span>
                        )}
                        <span className="text-[10px] text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {b.dakika} dk
                        </span>
                      </div>
                      <div className={`font-heading font-semibold mt-0.5 ${isDone ? "line-through text-slate-400" : "text-slate-900"}`}>
                        {b.baslik}
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{b.aciklama}</p>
                      {link && !isDone && (
                        <Link to={link} className="inline-flex items-center gap-1 text-xs text-[#FF6B6B] font-medium mt-2 hover:gap-1.5 transition-all"
                          data-testid={`plan-cta-${i}`}>
                          Şimdi başla <ArrowRight className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {plan.kapanis_mesaji && (
            <Card className="p-5 rounded-2xl border-slate-200 bg-slate-900 text-white">
              <p className="text-sm">{plan.kapanis_mesaji}</p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
